<template>
  <!-- Overlay mobile -->
  <transition name="overlay-fade">
    <div
      v-if="mobileOpen && isMobile"
      class="sidebar-overlay"
      @click="$emit('close-mobile')"
    />
  </transition>

  <aside
    class="sidebar"
    :class="{
      'is-collapsed': isCollapsed && !isMobile,
      'is-mobile-open': mobileOpen && isMobile,
    }"
  >
    <!-- Header -->
    <div class="sidebar-header">
      <img src="@/assets/img/Evolve/EGPro.png" alt="EGPro" class="logo-img" />
      <button
        v-if="!isMobile"
        class="toggle-btn"
        @click="$emit('toggle-collapse')"
        :title="isCollapsed ? 'Expandir' : 'Colapsar'"
      >
        <i :class="isCollapsed ? 'bi bi-text-indent-left' : 'bi bi-text-indent-right'" />
      </button>
      <button v-else class="toggle-btn" @click="$emit('close-mobile')">
        <i class="bi bi-x-lg" />
      </button>
    </div>

    <div class="divider" />

    <!-- Nav agrupado -->
    <nav class="sidebar-nav">
      <template v-for="grupo in navGroups" :key="grupo.label">
        <transition name="fade-slide">
          <p v-if="!isCollapsed || isMobile" class="nav-group-label">{{ grupo.label }}</p>
        </transition>
        <router-link
          v-for="item in grupo.items"
          :key="item.to"
          :to="item.to"
          class="nav-item"
          :class="{ 'is-active': esActivo(item.to) }"
          @click="$emit('close-mobile')"
        >
          <i :class="['bi', item.icon]" class="nav-icon-i" />
          <transition name="fade-slide">
            <span v-if="!isCollapsed || isMobile" class="nav-label">{{
              item.label
            }}</span>
          </transition>
          <span v-if="item.badge && (!isCollapsed || isMobile)" class="nav-badge">
            {{ item.badge }}
          </span>
        </router-link>
      </template>
    </nav>

    <!-- Footer -->
    <div class="sidebar-footer">
      <div class="divider" />
      <div class="admin-info">
        <div class="admin-avatar">{{ inicialAdmin }}</div>
        <transition name="fade-slide">
          <div v-if="!isCollapsed || isMobile" class="admin-meta">
            <p class="admin-nombre">{{ adminNombre }}</p>
            <p class="admin-rol">{{ adminRol }}</p>
          </div>
        </transition>
      </div>
      <button class="logout-btn" @click="$emit('logout')" title="Cerrar sesión">
        <i class="bi bi-box-arrow-right" />
        <transition name="fade-slide">
          <span v-if="!isCollapsed || isMobile">Salir</span>
        </transition>
      </button>
    </div>
  </aside>
</template>

<script setup>
import { computed } from "vue";
import { useRoute } from "vue-router";

const props = defineProps({
  isCollapsed: { type: Boolean, default: false },
  mobileOpen: { type: Boolean, default: false },
  screenWidth: { type: Number, default: 1280 },
  // [{ label: 'Gestión de evento', items: [{ to, icon, label, badge? }] }, ...]
  navGroups: { type: Array, default: () => [] },
  adminNombre: { type: String, default: "Admin" },
  adminRol: { type: String, default: "Administrador" },
});

defineEmits(["toggle-collapse", "close-mobile", "logout"]);

const route = useRoute();
const isMobile = computed(() => props.screenWidth <= 1024);

// EGPro usa rutas por path (no nombradas), así que el match es exacto,
// igual que ya lo hacía DashboardLayout.vue con esActivo().
function esActivo(path) {
  return route.path === path;
}

const inicialAdmin = computed(() => props.adminNombre?.charAt(0)?.toUpperCase() || "A");
</script>

<style scoped>
/* ─── Sidebar oscuro con acento dorado (paleta original) ────── */
.sidebar {
  --w: 240px;
  --w-col: 72px;
  --bg: #060d1a;
  --border: rgba(255, 255, 255, 0.1);
  --text: #ffffff;
  --text-dim: rgba(255, 255, 255, 0.5);
  --accent: #f3bc2c;
  --accent-soft: rgba(243, 188, 44, 0.18);
  --hover-bg: rgba(255, 255, 255, 0.08);

  position: fixed;
  inset: 0 auto 0 0;
  width: var(--w);
  height: 100vh;

  background: var(--bg);
  border-right: 1px solid var(--border);
  box-shadow: 2px 0 16px rgba(0, 0, 0, 0.3);

  display: flex;
  flex-direction: column;
  z-index: 50;
  overflow: hidden;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.sidebar.is-collapsed {
  width: var(--w-col);
}

/* Mobile drawer */
@media (max-width: 1024px) {
  .sidebar {
    width: min(85vw, 280px) !important;
    transform: translateX(-100%);
  }
  .sidebar.is-mobile-open {
    transform: translateX(0);
    box-shadow: 8px 0 32px rgba(0, 0, 0, 0.6);
  }
}

/* Overlay */
.sidebar-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  z-index: 45;
}
.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.22s ease;
}
.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}

/* Transiciones de contenido al colapsar */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(-6px);
}

.divider {
  height: 1px;
  background: var(--border);
  flex-shrink: 0;
}

/* ── Header ─────────────────────────────────────────── */
.sidebar-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 14px;
  height: 68px;
  flex-shrink: 0;
}
.logo-img {
  height: 30px;
  width: auto;
  object-fit: contain;
  flex-shrink: 0;
}
/* ✅ Al colapsar, el logo se oculta y el botón de apertura queda solo,
   centrado (antes se quedaba pegado a la izquierda sin el logo). */
.sidebar.is-collapsed .logo-img {
  display: none;
}
.sidebar.is-collapsed .toggle-btn {
  margin: 0 auto;
}
.sidebar-title {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 2px;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
}
.toggle-btn {
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: var(--text-dim);
  cursor: pointer;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  font-size: 1.15rem;
  margin-left: auto;
  transition: background 0.2s, color 0.2s;
}
.toggle-btn:hover {
  background: var(--hover-bg);
  color: var(--text);
}

/* ── Nav ────────────────────────────────────────────── */
.sidebar-nav {
  flex: 1;
  padding: 12px 8px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.sidebar-nav::-webkit-scrollbar {
  display: none;
}

.nav-group-label {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--text-dim);
  padding: 10px 12px 4px;
  margin: 0;
  white-space: nowrap;
}

.nav-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  text-decoration: none;
  color: var(--text);
  font-size: 13.5px;
  font-weight: 600;
  background: transparent;
  transition: background 0.18s, color 0.18s;
  white-space: nowrap;
}
.nav-item:hover {
  background: var(--hover-bg);
}
.nav-item.is-active {
  background: var(--accent-soft);
  color: var(--accent);
  font-weight: 700;
  box-shadow: inset 3px 0 0 var(--accent);
}
.nav-item.is-active .nav-icon-i {
  color: var(--accent);
}

.nav-icon-i {
  font-size: 1.05rem;
  flex-shrink: 0;
  width: 20px;
  text-align: center;
  transition: color 0.18s;
}
.nav-label {
  flex: 1;
}
.nav-badge {
  background: #ef4444;
  color: #fff;
  font-size: 10.5px;
  font-weight: 700;
  border-radius: 10px;
  padding: 1px 7px;
  flex-shrink: 0;
}

/* ── Footer ─────────────────────────────────────────── */
.sidebar-footer {
  flex-shrink: 0;
  padding: 10px 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.admin-info {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 4px 0;
  overflow: hidden;
}
.admin-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--accent-soft);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
}
.admin-meta {
  min-width: 0;
}
.admin-nombre {
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.admin-rol {
  font-size: 10.5px;
  color: var(--accent);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0;
  font-weight: 600;
  white-space: nowrap;
}

.logout-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 9px 12px;
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--text-dim);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.18s, border-color 0.18s, color 0.18s;
  white-space: nowrap;
}
.logout-btn:hover {
  background: rgba(239, 68, 68, 0.08);
  border-color: #ef4444;
  color: #dc2626;
}
.logout-btn i {
  font-size: 1.1rem;
  flex-shrink: 0;
}

/* Cuando está colapsado, centrar avatar/botón sin la etiqueta */
.sidebar.is-collapsed .admin-info {
  justify-content: center;
  padding: 8px 0 0;
}
.sidebar.is-collapsed .logout-btn {
  justify-content: center;
  padding: 9px;
}
</style>
